var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { ReactiveElement, css, property, } from '@folkjs/dom/ReactiveElement';
import { ResizeManager } from '@folkjs/dom/ResizeManger';
import { average, toDOMPrecision } from '@folkjs/geometry/utilities';
import * as V from '@folkjs/geometry/Vector2';
import { getStroke } from 'perfect-freehand';
export function toSVGPath(vertices) {
    const len = vertices.length;
    if (len < 4)
        return '';
    const a = vertices[0];
    const b = vertices[1];
    const c = vertices[2];
    let result = `M${toDOMPrecision(a.x)},${toDOMPrecision(a.y)} Q${toDOMPrecision(b.x)},${toDOMPrecision(b.y)} ${toDOMPrecision(average(b.x, c.x))},${toDOMPrecision(average(b.y, c.y))} T`;
    for (let i = 2, max = len - 1; i < max; i++) {
        const p1 = vertices[i];
        const p2 = vertices[i + 1];
        result += ` ${toDOMPrecision(average(p1.x, p2.x))},${toDOMPrecision(average(p1.y, p2.y))}`;
    }
    result += 'Z';
    return result;
}
const converter = {
    fromAttribute(value) {
        if (value === null)
            return [];
        return value.split(' ').map((point) => {
            const [x, y, pressure] = point.split(',').map(Number);
            return {
                x: Number(x),
                y: Number(y),
                pressure: Number.isNaN(pressure) ? undefined : pressure,
            };
        });
    },
    toAttribute(value) {
        return value
            .map((point) => {
            let str = `${toDOMPrecision(point.x)},${toDOMPrecision(point.y)}`;
            if (point.pressure !== undefined)
                str += `,${toDOMPrecision(point.pressure)}`;
            return str;
        })
            .join(' ');
    },
};
const rm = new ResizeManager();
export class FolkInk extends ReactiveElement {
    constructor() {
        super(...arguments);
        this.size = 8;
        this.thinning = 0.5;
        this.smoothing = 0;
        this.color = 'black';
        this.points = [];
        this.#stroke = document.createElement('div');
        this.#strokeOverlay = document.createElement('div');
        this.#onTransform = () => {
            this.requestUpdate();
        };
    }
    static { this.tagName = 'folk-ink'; }
    static { this.styles = css `
    :host {
      display: block;
      height: 100%;
      width: 100%;
      pointer-events: none;
    }

    [part='stroke'],
    [part='stroke-overlay'] {
      position: absolute;
      inset: 0;
      pointer-events: all;
      overflow: hidden;
    }

    [part='stroke'] {
      background-color: black;
    }

    [part='stroke-overlay'] {
      background-color: rgba(0, 0, 0, 0.1);
      opacity: 0;
      transition: opacity 0.1s ease-out;

      &:hover {
        opacity: 1;
      }
    }
  `; }
    #stroke;
    #strokeOverlay;
    createRenderRoot() {
        const root = super.createRenderRoot();
        this.#stroke.part.add('stroke');
        this.#strokeOverlay.part.add('stroke-overlay');
        root.append(this.#stroke, this.#strokeOverlay);
        this.addEventListener('transform', this.#onTransform);
        return root;
    }
    connectedCallback() {
        super.connectedCallback();
        rm.observe(this, this.#onTransform);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        rm.unobserve(this, this.#onTransform);
    }
    update(changedProperties) {
        super.update(changedProperties);
        if (changedProperties.has('color')) {
            this.#stroke.style.backgroundColor = this.color;
        }
        if (this.points.length === 0) {
            this.#stroke.style.clipPath = this.#strokeOverlay.style.clipPath = '';
            this.#stroke.style.display = this.#strokeOverlay.style.display = 'none';
            return;
        }
        this.#stroke.style.display = this.#strokeOverlay.style.display = '';
        const width = this.offsetWidth;
        const height = this.offsetHeight;
        const vertices = this.points.map((p) => ({
            x: p.x * width,
            y: p.y * height,
        }));
        const stroke = getStroke(vertices, {
            size: this.size,
            thinning: this.thinning,
            smoothing: this.smoothing,
            simulatePressure: true,
            start: {
                cap: true,
            },
            end: {
                cap: true,
            },
        }).map(([x, y]) => ({ x, y }));
        this.#stroke.style.clipPath = 'path("' + toSVGPath(stroke) + '")';
        const strokeOverlay = getStroke(vertices, {
            size: this.size + 5,
            thinning: 0,
            smoothing: this.smoothing,
            simulatePressure: true,
            start: {
                cap: true,
            },
            end: {
                cap: true,
            },
        }).map(([x, y]) => ({ x, y }));
        this.#strokeOverlay.style.clipPath = 'path("' + toSVGPath(strokeOverlay) + '")';
    }
    #onTransform;
}
__decorate([
    property({ type: Number, reflect: true })
], FolkInk.prototype, "size", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FolkInk.prototype, "thinning", void 0);
__decorate([
    property({ type: Number, reflect: true })
], FolkInk.prototype, "smoothing", void 0);
__decorate([
    property({ type: String, reflect: true })
], FolkInk.prototype, "color", void 0);
__decorate([
    property({ type: Array, reflect: true, converter })
], FolkInk.prototype, "points", void 0);
//# sourceMappingURL=folk-ink.js.map